'use client';

import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  liveTaskExecutionsFromMessages,
  mergeTrackedAndLiveExecutions,
  trackedAgentExecutionsForChat,
  useRunningAgents,
  type ParallelAgentExecution,
  type RunningAgentsState,
} from '@terra/ui-design-system';
import { useCallback, useMemo } from 'react';
import { useBackgroundExecutionStore } from '../../store/backgroundExecutionStore';
import { useAbortSubagentExecution } from './useAbortSubagentExecution';

export type CopilotRunningAgentsState = RunningAgentsState & {
  onStop: (execution: ParallelAgentExecution) => void;
  canStopExecution: (execution: ParallelAgentExecution) => boolean;
};

export function useCopilotRunningAgents({
  chatId,
  messages = [],
  targetId,
  targetType,
}: {
  chatId: string;
  messages?: TerraUIMessage[];
  targetId?: string;
  targetType?: string;
}): CopilotRunningAgentsState {
  const pendingRevision = useBackgroundExecutionStore(
    (state) => state.pendingRevision,
  );
  const executions = useMemo(() => {
    void pendingRevision;

    return mergeTrackedAndLiveExecutions({
      tracked: trackedAgentExecutionsForChat({
        executions: useBackgroundExecutionStore
          .getState()
          .pendingExecutionsByToolCallId.values(),
        chatId,
      }),
      live: liveTaskExecutionsFromMessages({ messages, chatId }),
    });
  }, [chatId, messages, pendingRevision]);
  const runningAgents = useRunningAgents({ executions });
  const abortExecution = useAbortSubagentExecution({
    chatId,
    targetId,
    targetType,
  });
  const canStopExecution = useCallback(
    (execution: ParallelAgentExecution) => execution.kind === 'subagent',
    [],
  );
  const onStop = useCallback(
    (execution: ParallelAgentExecution) => {
      if (execution.kind !== 'subagent') {
        return;
      }

      const pending = useBackgroundExecutionStore
        .getState()
        .getPendingExecution(execution.toolCallId);

      abortExecution(
        pending ?? {
          copilotThreadId: execution.copilotThreadId,
          agentThreadId: execution.agentThreadId,
          toolCallId: execution.toolCallId,
          applicationId: '',
          agentType: execution.agentType,
          kind: 'subagent',
          waitingInput: execution.waitingInput,
          terminalStatus: execution.terminalStatus,
        },
      );
    },
    [abortExecution],
  );

  return { ...runningAgents, onStop, canStopExecution };
}
